import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Grid,
    Card,
    CardContent,
    Tooltip,
    Collapse,
    IconButton
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Security as AdminIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useUserPermissions } from './hooks/useUserPermissions';

export const UserPermissionsConfig = ({
    user,
    selectedRole,
    onRoleChange,
    advancedMode,
    onAdvancedModeChange,
    customCanManageTeam,
    onCustomCanManageTeamChange,
    filePermissions,
    onFilePermissionChange,
    allFiles,
    getDefaultPermissionsForRole,
    currentUserAccess
}) => {
    const { canConfigureFilePermissions } = useUserPermissions(currentUserAccess);

    const roles = [
        {
            value: 'Viewer',
            label: 'Гость',
            description: 'Только просмотр файлов',
            icon: <ViewIcon fontSize="large" />
        },
        {
            value: 'Editor',
            label: 'Редактор',
            description: 'Просмотр, редактирование, удаление своих версий',
            icon: <EditIcon fontSize="large" />
        },
        {
            value: 'Admin',
            label: 'Администратор',
            description: 'Полный контроль над файлами + управление командой',
            icon: <AdminIcon fontSize="large" />
        }
    ];

    // Функция для получения текущих прав с учетом кастомных изменений
    const getCurrentPermissions = (fileKey, file) => {
        // Если файл недоступен - всегда возвращаем false
        if (!canConfigureFilePermissions(file)) {
            return {
                canRead: false,
                canEdit: false,
                canDeleteOwnVersions: false,
                canDeleteAllVersions: false
            };
        }

        const defaultPermissions = getDefaultPermissionsForRole(selectedRole);

        // Если есть кастомные настройки для этого файла, смешиваем их с правами по умолчанию
        if (filePermissions && filePermissions[fileKey]) {
            return {
                ...defaultPermissions,
                ...filePermissions[fileKey]
            };
        }

        return defaultPermissions;
    };


    // Обработчик изменения роли
    const handleRoleChange = (newRole) => {
        onRoleChange(newRole);
    };

    // Обработчик изменения прав файла с сохранением зависимостей
    const handleFilePermissionChange = (fileKey, permissionType, value, file) => {
        // Блокируем изменение недоступных файлов
        if (!canConfigureFilePermissions(file)) {
            return;
        }

        const currentPermissions = getCurrentPermissions(fileKey, file);
        const newPermissions = { ...currentPermissions };

        // Сохраняем оригинальное значение изменяемого права
        newPermissions[permissionType] = value;

        // Логика зависимостей прав
        if (permissionType === 'canRead' && !value) {
            // Если снимаем просмотр - снимаем все зависимые права
            newPermissions.canEdit = false;
            newPermissions.canDeleteOwnVersions = false;
            newPermissions.canDeleteAllVersions = false;
        } else if (permissionType === 'canEdit' && value) {
            // Если включаем редактирование - включаем просмотр
            newPermissions.canRead = true;
        } else if (permissionType === 'canDeleteOwnVersions' && value) {
            // Если включаем удаление своих версий - включаем просмотр
            newPermissions.canRead = true;
        } else if (permissionType === 'canDeleteAllVersions' && value) {
            // Если включаем удаление всех версий - включаем просмотр и удаление своих версий
            newPermissions.canRead = true;
            newPermissions.canDeleteOwnVersions = true;
        }

        // ВАЖНО: Сохраняем ВСЕ текущие права для этого файла
        // Это гарантирует, что при изменении одного права не сбросятся другие
        //const updatedFilePermissions = {
        //    ...(filePermissions || {}),
        //    [fileKey]: {
        //        ...newPermissions
        //    }
        //};

        // Передаем все обновленные права обратно
        Object.keys(newPermissions).forEach(key => {
            onFilePermissionChange(fileKey, key, newPermissions[key]);
        });
    };

    useEffect(() => {
        // Инициализируем права для всех файлов, если они еще не установлены
        allFiles.forEach(file => {
            const fileKey = `${file.fileName}_v${file.fileVersion}`;
            const currentPerms = filePermissions && filePermissions[fileKey];

            if (!currentPerms && canConfigureFilePermissions(file)) {
                const defaultPermissions = getDefaultPermissionsForRole(selectedRole);
                Object.keys(defaultPermissions).forEach(permissionType => {
                    onFilePermissionChange(fileKey, permissionType, defaultPermissions[permissionType]);
                });
            }
        });
    }, [allFiles, selectedRole]);

    // Обработчик переключения аккордеона
    const handleAdvancedModeToggle = () => {
        onAdvancedModeChange(!advancedMode);
    };

    return (
        <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={2}>
            <Typography variant="subtitle1">
                <strong>{user?.fullName || user?.fio || user?.FIO || 'Неизвестный пользователь'}</strong>
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
                {user?.login}
            </Typography>

            {/* Card Selection для выбора роли */}
            <Box mt={2} className="role-selection-section">
                <Typography variant="subtitle1" gutterBottom className="role-section-title">
                    Роль пользователя
                </Typography>
                <Grid container spacing={2} className="roles-grid">
                    {roles.map((role) => (
                        <Grid key={role.value} size={{ xs: 12, sm: 4 }} className="role-grid-item">
                            <Card
                                onClick={() => handleRoleChange(role.value)}
                                className={`role-card ${selectedRole === role.value ? 'role-card--selected' : ''} ${role.value.toLowerCase()}-card`}
                            >
                                <CardContent className="role-card-content">
                                    <Box className="role-icon-container">
                                        {role.icon}
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        className="role-title"
                                    >
                                        {role.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className="role-description"
                                    >
                                        {role.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Аккордеон для расширенных настроек */}
            <Box mt={3}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={handleAdvancedModeToggle}
                    className="pt-2 pd-1"
                    style={{ cursor: 'pointer' }}
                >
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                        Расширенные настройки прав доступа
                    </Typography>
                    <IconButton
                        size="small"
                        style={{
                            transform: advancedMode ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                </Box>

                <Collapse in={advancedMode}>
                    <Box mt={1} ml={1} mr={1}>
                        <Tooltip title="Разрешить добавлять, удалять и изменять права других участников">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={customCanManageTeam}
                                        onChange={(e) => onCustomCanManageTeamChange(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle1">
                                            Может управлять командой
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Tooltip>
                        {allFiles.length > 0 ? (
                            <Box mt={1}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '8px' }}>
                                                Имя файла
                                            </th>
                                            <th style={{ textAlign: 'center', padding: '8px', width: '80px' }}>
                                                <Tooltip title="Просмотр файла">
                                                    <Box>
                                                        <ViewIcon fontSize="medium" />
                                                    </Box>
                                                </Tooltip>
                                            </th>
                                            <th style={{ textAlign: 'center', padding: '8px', width: '80px' }}>
                                                <Tooltip title="Редактирование файла">
                                                    <Box>
                                                        <EditIcon fontSize="medium" />
                                                    </Box>
                                                </Tooltip>
                                            </th>
                                            <th style={{ textAlign: 'center', padding: '8px', width: '80px' }}>
                                                <Tooltip title="Удаление своих версий">
                                                    <Box>
                                                        <DeleteIcon fontSize="small" />
                                                        <Typography variant="caption" display="block">Свои</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </th>
                                            <th style={{ textAlign: 'center', padding: '8px', width: '80px' }}>
                                                <Tooltip title="Удаление всех версий">
                                                    <Box>
                                                        <DeleteIcon fontSize="small" />
                                                        <Typography variant="caption" display="block">Все</Typography>
                                                    </Box>
                                                </Tooltip>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allFiles.map((file) => {
                                            const fileKey = `${file.fileName}_v${file.fileVersion}`;
                                            const permissions = getCurrentPermissions(fileKey, file);
                                            const isFileAccessible = canConfigureFilePermissions(file);

                                            const safePermissions = {
                                                canRead: Boolean(permissions?.canRead),
                                                canEdit: Boolean(permissions?.canEdit),
                                                canDeleteOwnVersions: Boolean(permissions?.canDeleteOwnVersions),
                                                canDeleteAllVersions: Boolean(permissions?.canDeleteAllVersions)
                                            };

                                            return (
                                                <tr key={fileKey} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                    <td style={{ padding: '8px' }}>
                                                        {file.fileName}
                                                        {!isFileAccessible && (
                                                            <Typography variant="caption" color="textSecondary" display="block">
                                                                Недоступен
                                                            </Typography>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '8px' }}>
                                                        <Checkbox
                                                            checked={safePermissions.canRead}
                                                            onChange={(e) => handleFilePermissionChange(fileKey, 'canRead', e.target.checked, file)}
                                                            color="primary"
                                                            disabled={!isFileAccessible}
                                                            icon={<ViewIcon />}
                                                            checkedIcon={<ViewIcon />}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '8px' }}>
                                                        <Checkbox
                                                            checked={safePermissions.canEdit}
                                                            onChange={(e) => handleFilePermissionChange(fileKey, 'canEdit', e.target.checked, file)}
                                                            color="primary"
                                                            disabled={!isFileAccessible || !safePermissions.canRead}
                                                            icon={<EditIcon />}
                                                            checkedIcon={<EditIcon />}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '8px' }}>
                                                        <Checkbox
                                                            checked={safePermissions.canDeleteOwnVersions}
                                                            onChange={(e) => handleFilePermissionChange(fileKey, 'canDeleteOwnVersions', e.target.checked, file)}
                                                            color="primary"
                                                            disabled={!isFileAccessible || !safePermissions.canRead || safePermissions.canDeleteAllVersions}
                                                            icon={<DeleteIcon />}
                                                            checkedIcon={<DeleteIcon />}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center', padding: '8px' }}>
                                                        <Checkbox
                                                            checked={safePermissions.canDeleteAllVersions}
                                                            onChange={(e) => handleFilePermissionChange(fileKey, 'canDeleteAllVersions', e.target.checked, file)}
                                                            color="primary"
                                                            disabled={!isFileAccessible || !safePermissions.canRead}
                                                            icon={<DeleteIcon />}
                                                            checkedIcon={<DeleteIcon />}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </Box>
                        ) : (
                            <Box textAlign="center" py={3}>
                                <Typography variant="body2" color="textSecondary">
                                    Нет доступных файлов для настройки прав
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
};